import React, { use, useEffect, useMemo, useState } from "react";
import {
  AlertComponent,
  BreadcrumbComponent,
} from "../../../../common/components";
import { Box, Tab, Tabs } from "@mui/material";
import "./view-campaign-performance-metrics.scss";
import ViewCampaignRoi from "./view-campaign-roi/view-campaign-roi";
import LeadSourceAttribution from "./lead-source-attribution/lead-source-attribution";
import LeadConversionRate from "./lead-conversion-rate/lead-conversion-rate";
import CostPerLead from "./cost-per-lead/cost-per-lead";
import OpportunityCreationRate from "./opportunity-creation-rate/opportunity-creation-rate";
import RevenuePerCampaign from "./revenue-per-campaign/revenue-per-campaign";
import {
  exportMetricCampaigns,
  getCampaignPerformanceMetrics,
} from "../../../../api/campaign";
import { useSetLoading } from "../../../../common/hooks/appearance";
import ApexCharts from "apexcharts";
import CompanyReportFilter from "../../../../common/components/company-report-filter/company-report-filter";
import { useLogin } from "../../../../common/hooks/useUser";
import partnerReportEmptyIcon from "../../../../assets/icons/partner-reports-default.svg";
import EmailSetUpBanner from "../../../../common/components/email-setup-required-banner/email-setup-banner";
import { routes } from "../../../../constants/routes";
import { useResourcePermissions } from "../../../../common/hooks/resource-permissions";
import MODULES from "../../../../constants/modules";

const breadcrumbLink = [
  { label: "Campaigns", link: routes.CAMPAIGNLIST },
  { label: "Campaign Performance Metrics", link: "#" },
];

const ViewCampaignPerformanceMetrics = () => {
  const [metricsData, setMetricsData] = React.useState({});
  const [isLoading, setIsLoading] = React.useState(true);
  const [loading, setLoading] = useState(false);
  const [indexValue, setIndexValue] = React.useState(0);
  const [partnerId, setPartnerId] = useState<number | null>(null);
  const [partnerName, setPartnerName] = useState<string | null>(null);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);

  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setIndexValue(newValue);
    if (newValue === 0) {
      setPartnerId(null);
      setPartnerName(null);
    }
  };
  const loginDetails = useLogin();
  const tenantDetails = loginDetails?.tenantDetails;
  const userDetails = loginDetails?.userDetails;
  const isEmailConfigured = loginDetails?.isEmailConfigured;
  const isAFPSuperAdmin =
    tenantDetails?.tenantType === "SuperTenant" &&
    userDetails?.roles?.role === "Super Admin";
  const isSuperAdmin = userDetails?.roles?.role === "Super Admin";
  const { canExport } = useResourcePermissions(MODULES.EXPORT);
  const renderTabContent = () => {
    switch (indexValue) {
      case 0:
        return <>{renderReports()}</>;
      case 1:
        return (
          <>
            <div className="partner-report-company-filter">
              <CompanyReportFilter
                setCompany={(partner) => {
                  setPartnerId(partner?.id);
                  setPartnerName(partner?.name);
                }}
              />
            </div>
            {partnerId ? (
              <>{renderReports()}</>
            ) : (
              <Box className="partner-report-empty-state">
                <img src={partnerReportEmptyIcon} alt="No Partner Selected" />
              </Box>
            )}
          </>
        );
      default:
        return <>{renderReports()}</>;
    }
  };

  const renderReports = () => {
    return (
      <>
        <Box className="lead-cpl-wrapper-campaign" gap={3}>
          <LeadConversionRate
            metricsData={metricsData}
            isLoading={isLoading}
            exportChart={exportChart}
            enableExport={canExport}
          />
          <CostPerLead
            isLoading={isLoading}
            metricsData={metricsData}
            exportChart={exportChart}
            enableExport={canExport}
          />
        </Box>
        <Box className="roi-lead-source-wrapper" gap={3}>
          <ViewCampaignRoi
            metricsData={metricsData}
            isLoading={isLoading}
            exportChart={exportChart}
            enableExport={canExport}
          />
          <LeadSourceAttribution
            metricsData={metricsData}
            isLoading={isLoading}
            exportChart={exportChart}
            enableExport={canExport}
          />
        </Box>
        <OpportunityCreationRate
          isLoading={isLoading}
          metricsData={metricsData}
          exportChart={exportChart}
          enableExport={canExport}
        />
        <RevenuePerCampaign
          metricsData={metricsData}
          exportChart={exportChart}
          enableExport={canExport}
        />
      </>
    );
  };

  const fetchMetricsData = async () => {
    try {
      setIsLoading(true);
      const resp = await getCampaignPerformanceMetrics({ partnerId });
      setMetricsData(resp?.data?.data);
    } catch (error) {
      console.error("Error fetching campaign performance metrics:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const exportChart = async (params: any, id?: string, base64Image?: any) => {
    try {
      setLoading(true);
      let data: any = {};
      if (id && !base64Image) {
        const { imgURI, blob } = await ApexCharts.exec(id, "dataURI");
        data.imageData = imgURI.split(",")[1];
      }
      if (base64Image) {
        data.imageData = base64Image;
      }
      const resp = await exportMetricCampaigns({ ...params, partnerId }, data);
      setAlertMessage(
        partnerName
          ? `${partnerName} report generated successfully`
          : "Report generated successfully"
      );
      setAlertType("success");
      setAlertOpen(true);
    } catch (error) {
      console.error("Error exporting chart:", error);
      // More specific error handling
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchMetricsData();
  }, [partnerId]);

  const showLoading = useMemo(() => isLoading || loading, [isLoading, loading]);
  useSetLoading(showLoading);

  return (
    <div className="view-campaign-performance-metrics">
      <div className="breadcrumb-wrapper">
        <BreadcrumbComponent breadcrumbLink={breadcrumbLink} />
      </div>
      <Box className="body-wrapper">
        {isAFPSuperAdmin ? (
          <>
            <Box>
              <Tabs
                value={indexValue}
                onChange={handleTabChange}
                allowScrollButtonsMobile
                className="lead-reports-tabs"
                sx={{
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 500,
                  },
                  "& .MuiTab-root.Mui-selected": {
                    color: "black",
                    borderBottom: "2px solid #785607",
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#785607",
                  },
                }}
              >
                <Tab iconPosition="start" label="AFP Reports" />
                <Tab iconPosition="start" label="Partner Reports" />
              </Tabs>
              {!isEmailConfigured && isSuperAdmin && (
                <div className="email-setup-banner-wrapper">
                  <EmailSetUpBanner />
                </div>
              )}
            </Box>
            <Box>{renderTabContent()}</Box>
          </>
        ) : (
          <>
            {!isEmailConfigured && isSuperAdmin && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <EmailSetUpBanner />
              </div>
            )}
            <Box>{renderReports()}</Box>
          </>
        )}
        <AlertComponent
          message={alertMessage}
          type={alertType ?? "info"}
          open={alertOpen}
          onClose={() => setAlertOpen(false)}
        />
      </Box>
    </div>
  );
};

export default ViewCampaignPerformanceMetrics;
